/**
 * Menu "Exportar" padrão de listas, mapas e painéis.
 *
 * `<ExportMenu screen="home" snapshot={() => ({ title, filters, kind, data })} formats={['csv', 'html']} handlers={…} />`
 * - `snapshot()`: o recorte que a tela mostra agora (ver exportHtml.ts); chamado só ao exportar;
 * - `formats`: as opções que fazem sentido na tela (padrão: tabela → CSV e HTML; mapa e painel → HTML). Os que
 *   aparecem em `handlers` também entram;
 * - embutidos: `csv` (de um snapshot de tabela) e `html` (o HTML interativo autocontido, de qualquer snapshot);
 * - `handlers`: o jeito da tela para os outros formatos (`xlsx`/`pdf` pelo relatório do núcleo, `png`/`svg` pela
 *   exportação do mapa) ou para trocar um embutido; recebem o snapshot.
 * Toda exportação fica na trilha de auditoria pelo `audit` do `ExportProvider` (a casca liga em POST /api/v1/me/exports)
 * — a do núcleo (relatórios) já audita por conta própria, então o `handler` pode devolver `{ audited: true }`.
 *
 * `<ExportProvider value={{ product, brandColor, logo, who, audit }}>`: a casca diz a marca (com o logotipo como data URI),
 * quem exporta e onde auditar.
 */
import { useI18n, intlLocale, type CommonKey } from '@hive/i18n';
import { createContext, useContext, type ReactNode } from 'react';
import { buildExportHtml, downloadText, exportFileName, tableToCsv, type ExportFormat, type ExportSnapshot } from './exportHtml';
import { Icon } from './icons';
import { MenuButton, useToast } from './overlay';

export interface ExportAuditRecord {
  screen: string;
  title: string;
  format: ExportFormat;
  rows?: number;
  filters?: Record<string, string>;
}

export interface ExportContextValue {
  product?: string;
  brandColor?: string | null;
  /** Logotipo completo da empresa como data URI, para o cabeçalho dos arquivos exportados. */
  logo?: string | null;
  /** Nome de quem exporta (vai no rodapé do arquivo). */
  who?: string;
  audit?: (r: ExportAuditRecord) => Promise<unknown> | void;
}

const ExportCtx = createContext<ExportContextValue>({});

/** Marca e quem exporta (para exportações montadas fora do menu, como a página do mapa de serviços). */
export function useExportContext(): ExportContextValue {
  return useContext(ExportCtx);
}

export function ExportProvider({ value, children }: { value: ExportContextValue; children: ReactNode }) {
  return <ExportCtx.Provider value={value}>{children}</ExportCtx.Provider>;
}

export type ExportHandler = (snap: ExportSnapshot) => Promise<{ audited?: boolean } | void> | { audited?: boolean } | void;

/** Quantos itens o recorte leva (linhas, nós ou cartões), para a trilha. */
export function snapshotSize(s: ExportSnapshot): number {
  return s.kind === 'table' ? s.data.rows.length : s.kind === 'map' ? s.data.nodes.length : s.data.cards.length;
}

const ORDER: ExportFormat[] = ['csv', 'xlsx', 'pdf', 'png', 'svg', 'html'];

export function ExportMenu({
  screen,
  snapshot,
  kind,
  formats,
  handlers = {},
  className,
}: {
  /** Identificador da tela na trilha ("home", "assets.inventory"). */
  screen: string;
  snapshot: () => ExportSnapshot;
  /** Tipo do recorte, para escolher os formatos padrão sem montar o snapshot. */
  kind: ExportSnapshot['kind'];
  formats?: ExportFormat[];
  handlers?: Partial<Record<ExportFormat, ExportHandler>>;
  className?: string;
}) {
  const { t, lang } = useI18n<CommonKey>();
  const ctx = useContext(ExportCtx);
  const toast = useToast();
  const base: ExportFormat[] = formats ?? (kind === 'table' ? ['csv', 'html'] : ['html']);
  const list = ORDER.filter((f) => base.includes(f) || handlers[f]).filter((f) => handlers[f] || f === 'html' || (f === 'csv' && kind === 'table'));

  const run = async (f: ExportFormat) => {
    try {
      const snap = snapshot();
      let audited = false;
      const h = handlers[f];
      if (h) {
        audited = !!(await h(snap))?.audited;
      } else if (f === 'csv' && snap.kind === 'table') {
        const name = exportFileName(snap.title, 'csv');
        downloadText(name, tableToCsv(snap.data, lang), 'text/csv;charset=utf-8');
        toast(t('ui.export.done', { name }), { kind: 'ok' });
      } else if (f === 'html') {
        const when = new Intl.DateTimeFormat(intlLocale(lang), { dateStyle: 'short', timeStyle: 'short' }).format(new Date());
        const fl = Object.entries(snap.filters ?? {});
        const html = buildExportHtml(snap, {
          lang,
          brandColor: ctx.brandColor,
          logo: ctx.logo,
          brandName: ctx.product ?? 'Hive',
          texts: {
            footer: t('ui.export.footer', {
              product: ctx.product ?? 'Hive',
              when,
              who: ctx.who ?? '—',
              filters: fl.length ? fl.map(([k, v]) => `${k}: ${v}`).join(' · ') : t('ui.export.noFilters'),
            }),
            search: t('ui.export.search'),
            rows: t('ui.export.rows'),
            layers: t('ui.export.layers'),
            zoomIn: t('ui.export.zoomIn'),
            zoomOut: t('ui.export.zoomOut'),
            reset: t('ui.export.reset'),
            theme: t('ui.export.theme'),
            close: t('ui.export.close'),
            filters: t('ui.export.filters'),
          },
        });
        const name = exportFileName(snap.title, 'html');
        downloadText(name, html, 'text/html;charset=utf-8');
        toast(t('ui.export.done', { name }), { kind: 'ok' });
      } else {
        return;
      }
      // a trilha não impede o arquivo: falha ao registrar não vira erro para quem exportou
      if (!audited) await Promise.resolve(ctx.audit?.({ screen, title: snap.title, format: f, rows: snapshotSize(snap), filters: snap.filters })).catch(() => undefined);
    } catch {
      toast(t('ui.export.failed'), { kind: 'crit' });
    }
  };

  if (!list.length) return null;
  return (
    <MenuButton
      className={className ?? 'btn'}
      menuLabel={t('ui.export.menu')}
      button={
        <>
          <Icon name="download" size="sm" /> {t('ui.export.button')}
        </>
      }
    >
      {(close) => (
        <>
          {list.map((f) => (
            <button
              key={f}
              type="button"
              role="menuitem"
              className="pop-item"
              onClick={() => {
                close();
                void run(f);
              }}
            >
              {t(`ui.export.${f}`)}
            </button>
          ))}
        </>
      )}
    </MenuButton>
  );
}
